/**
 * Session Monitor - Admin Page (DC-630)
 *
 * Admin interface for monitoring and managing user sessions:
 * - View all active sessions in the organization
 * - Filter by user, IP address, device type, date range
 * - View session details with activity timeline
 * - Force terminate sessions (individual or by user)
 *
 * @version 1.0.0
 * @date 2026-01-04
 * @epic DC-6 Multi-Tenancy & IAM
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthenticatedFetch } from '../../hooks/useAuth';
import './session-monitor.css';

// =============================================================================
// TYPES
// =============================================================================

type DeviceType = 'desktop' | 'mobile' | 'tablet' | 'unknown';

interface DeviceInfo {
  type: DeviceType;
  browser: string;
  os: string;
  raw: string;
}

interface Session {
  sessionId: string;
  userId: string;
  userEmail: string;
  orgId: string;
  role: string;
  deviceInfo: DeviceInfo;
  ipAddress: string;
  createdAt: string;
  lastActivityAt: string;
  expiresAt: string;
  isActive: boolean;
}

interface SessionActivity {
  event: string;
  timestamp: string;
  sessionId?: string;
  ipAddress?: string;
  details?: string;
}

interface SessionSummary {
  totalActive: number;
  byDeviceType: Record<DeviceType, number>;
}

interface SessionFilters {
  userId?: string;
  ipAddress?: string;
  deviceType?: DeviceType;
  createdAfter?: string;
  createdBefore?: string;
}

interface Pagination {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

// Device type display info
const DEVICE_TYPE_INFO: Record<DeviceType, { label: string; icon: string }> = {
  desktop: { label: 'Desktop', icon: '🖥️' },
  mobile: { label: 'Mobile', icon: '📱' },
  tablet: { label: 'Tablet', icon: '📱' },
  unknown: { label: 'Unknown', icon: '❓' },
};

// =============================================================================
// FILTER BAR COMPONENT
// =============================================================================

interface FilterBarProps {
  filters: SessionFilters;
  onFilterChange: (filters: SessionFilters) => void;
  onClear: () => void;
}

const FilterBar: React.FC<FilterBarProps> = ({ filters, onFilterChange, onClear }) => {
  const [localFilters, setLocalFilters] = useState<SessionFilters>(filters);

  const handleApply = () => {
    onFilterChange(localFilters);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApply();
    }
  };

  const hasFilters =
    filters.userId || filters.ipAddress || filters.deviceType || filters.createdAfter || filters.createdBefore;

  return (
    <div className="filter-bar">
      <div className="filter-group">
        <label htmlFor="filter-user">User Email</label>
        <input
          id="filter-user"
          type="text"
          className="filter-input"
          placeholder="Search by email..."
          value={localFilters.userId || ''}
          onChange={(e) => setLocalFilters({ ...localFilters, userId: e.target.value || undefined })}
          onKeyPress={handleKeyPress}
        />
      </div>

      <div className="filter-group">
        <label htmlFor="filter-ip">IP Address</label>
        <input
          id="filter-ip"
          type="text"
          className="filter-input"
          placeholder="e.g., 192.168.1.1"
          value={localFilters.ipAddress || ''}
          onChange={(e) => setLocalFilters({ ...localFilters, ipAddress: e.target.value || undefined })}
          onKeyPress={handleKeyPress}
        />
      </div>

      <div className="filter-group">
        <label htmlFor="filter-device">Device Type</label>
        <select
          id="filter-device"
          className="filter-select"
          value={localFilters.deviceType || ''}
          onChange={(e) =>
            setLocalFilters({ ...localFilters, deviceType: (e.target.value as DeviceType) || undefined })
          }
        >
          <option value="">All Devices</option>
          <option value="desktop">Desktop</option>
          <option value="mobile">Mobile</option>
          <option value="tablet">Tablet</option>
          <option value="unknown">Unknown</option>
        </select>
      </div>

      <div className="filter-group">
        <label htmlFor="filter-after">Created After</label>
        <input
          id="filter-after"
          type="datetime-local"
          className="filter-input"
          value={localFilters.createdAfter || ''}
          onChange={(e) => setLocalFilters({ ...localFilters, createdAfter: e.target.value || undefined })}
        />
      </div>

      <div className="filter-group">
        <label htmlFor="filter-before">Created Before</label>
        <input
          id="filter-before"
          type="datetime-local"
          className="filter-input"
          value={localFilters.createdBefore || ''}
          onChange={(e) => setLocalFilters({ ...localFilters, createdBefore: e.target.value || undefined })}
        />
      </div>

      <div className="filter-actions">
        <button className="btn btn-primary" onClick={handleApply}>
          Apply
        </button>
        {hasFilters && (
          <button
            className="btn btn-secondary"
            onClick={() => {
              setLocalFilters({});
              onClear();
            }}
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// SESSION DETAIL MODAL
// =============================================================================

interface SessionDetailModalProps {
  session: Session;
  timeline: SessionActivity[];
  onClose: () => void;
  onTerminate: () => void;
}

const SessionDetailModal: React.FC<SessionDetailModalProps> = ({ session, timeline, onClose, onTerminate }) => {
  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleString();

  const getSessionDuration = () => {
    const start = new Date(session.createdAt);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Session Details</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          <div className="detail-section">
            <h3>User Information</h3>
            <div className="detail-grid">
              <div className="detail-row">
                <span className="detail-label">Email</span>
                <span className="detail-value">{session.userEmail}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Role</span>
                <span className="detail-value capitalize">{session.role}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">User ID</span>
                <span className="detail-value mono">{session.userId}</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Session Information</h3>
            <div className="detail-grid">
              <div className="detail-row">
                <span className="detail-label">Session ID</span>
                <span className="detail-value mono">{session.sessionId.substring(0, 16)}...</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Status</span>
                <span className={`status-badge ${session.isActive ? 'active' : 'inactive'}`}>
                  {session.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Duration</span>
                <span className="detail-value">{getSessionDuration()}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Created</span>
                <span className="detail-value">{formatDate(session.createdAt)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Last Activity</span>
                <span className="detail-value">{formatDate(session.lastActivityAt)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Expires</span>
                <span className="detail-value">{formatDate(session.expiresAt)}</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Device & Location</h3>
            <div className="detail-grid">
              <div className="detail-row">
                <span className="detail-label">Device Type</span>
                <span className="detail-value">
                  {(DEVICE_TYPE_INFO[session.deviceInfo?.type as DeviceType] || DEVICE_TYPE_INFO.unknown).icon}{' '}
                  {(DEVICE_TYPE_INFO[session.deviceInfo?.type as DeviceType] || DEVICE_TYPE_INFO.unknown).label}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Browser</span>
                <span className="detail-value">{session.deviceInfo?.browser || 'Unknown'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Operating System</span>
                <span className="detail-value">{session.deviceInfo?.os || 'Unknown'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">IP Address</span>
                <span className="detail-value mono">{session.ipAddress}</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Activity Timeline</h3>
            {timeline.length === 0 ? (
              <p className="no-timeline">No recent activity recorded</p>
            ) : (
              <div className="activity-timeline">
                {timeline.map((event, index) => (
                  <div key={index} className="timeline-item">
                    <div className="timeline-marker" />
                    <div className="timeline-content">
                      <div className="timeline-event">{(event.event || 'Unknown event').replace(/_/g, ' ')}</div>
                      <div className="timeline-time">{formatDate(event.timestamp)}</div>
                      {event.details && <div className="timeline-details">{event.details}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {session.isActive && (
            <button className="btn btn-danger" onClick={onTerminate}>
              Terminate Session
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// CONFIRM MODAL
// =============================================================================

interface ConfirmModalProps {
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onCancel}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          <p>{message}</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? 'Processing...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const SessionMonitor: React.FC = () => {
  const { authFetch: authenticatedFetch } = useAuthenticatedFetch();

  // Data state
  const [sessions, setSessions] = useState<Session[]>([]);
  const [summary, setSummary] = useState<SessionSummary | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    limit: 20,
    offset: 0,
    has_more: false,
  });

  // Filter state
  const [filters, setFilters] = useState<SessionFilters>({});

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [detailSession, setDetailSession] = useState<{ session: Session; timeline: SessionActivity[] } | null>(
    null
  );
  const [confirmTerminate, setConfirmTerminate] = useState<Session | null>(null);
  const [confirmTerminateUser, setConfirmTerminateUser] = useState<{ userId: string; email: string } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // ==========================================================================
  // DATA FETCHING
  // ==========================================================================

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set('limit', pagination.limit.toString());
      params.set('offset', pagination.offset.toString());

      if (filters.userId) params.set('user_id', filters.userId);
      if (filters.ipAddress) params.set('ip_address', filters.ipAddress);
      if (filters.deviceType) params.set('device_type', filters.deviceType);
      if (filters.createdAfter) params.set('created_after', new Date(filters.createdAfter).toISOString());
      if (filters.createdBefore) params.set('created_before', new Date(filters.createdBefore).toISOString());

      const response = await authenticatedFetch(`/api/admin/sessions?${params.toString()}`);

      if (!response.ok) {
        throw new Error('Failed to fetch sessions');
      }

      const data = await response.json();
      setSessions(data.data.sessions);
      setPagination(data.data.pagination);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsLoading(false);
    }
  }, [authenticatedFetch, pagination.limit, pagination.offset, filters]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/admin/sessions/summary');

      if (response.ok) {
        const data = await response.json();
        setSummary(data.data);
      }
    } catch (err) {
      // Summary is not critical, don't show error
      console.error('Failed to fetch session summary:', err);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchSessions();
    fetchSummary();
  }, [fetchSessions, fetchSummary]);

  // ==========================================================================
  // ACTIONS
  // ==========================================================================

  const handleViewDetails = async (session: Session) => {
    try {
      const response = await authenticatedFetch(`/api/admin/sessions/${session.sessionId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch session details');
      }

      const data = await response.json();
      setDetailSession({
        session: data.data.session,
        timeline: data.data.timeline || [],
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Unknown error");
    }
  };

  const handleTerminateSession = async () => {
    if (!confirmTerminate) return;

    setIsProcessing(true);
    try {
      const response = await authenticatedFetch(`/api/admin/sessions/${confirmTerminate.sessionId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || 'Failed to terminate session');
      }

      setConfirmTerminate(null);
      setDetailSession(null);
      fetchSessions();
      fetchSummary();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTerminateUserSessions = async () => {
    if (!confirmTerminateUser) return;

    setIsProcessing(true);
    try {
      const response = await authenticatedFetch(`/api/admin/sessions/user/${confirmTerminateUser.userId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error?.message || 'Failed to terminate user sessions');
      }

      const data = await response.json();
      setConfirmTerminateUser(null);
      setError(null);
      // Show success message temporarily
      const msg = `Terminated ${data.data.terminated} session(s) for ${confirmTerminateUser.email}`;
      alert(msg);
      fetchSessions();
      fetchSummary();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFilterChange = (newFilters: SessionFilters) => {
    setFilters(newFilters);
    setPagination((p) => ({ ...p, offset: 0 })); // Reset to first page
  };

  const handleClearFilters = () => {
    setFilters({});
    setPagination((p) => ({ ...p, offset: 0 }));
  };

  // ==========================================================================
  // HELPERS
  // ==========================================================================

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getSessionAge = (createdAt: string) => {
    const start = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  if (isLoading && sessions.length === 0) {
    return (
      <div className="session-monitor">
        <div className="loading-container">
          <div className="spinner" />
          <p>Loading sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="session-monitor">
      <div className="session-header">
        <div>
          <h1>Session Monitor</h1>
          <p>Monitor and manage active user sessions across your organization</p>
        </div>
        <button className="btn btn-secondary" onClick={() => { fetchSessions(); fetchSummary(); }}>
          Refresh
        </button>
      </div>

      {error && (
        <div className="error-container">
          <span className="error-icon">!</span>
          <span>{error}</span>
          <button className="error-dismiss" onClick={() => setError(null)}>
            &times;
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="session-summary">
        <div className="summary-card">
          <div className="summary-icon">👥</div>
          <div className="summary-content">
            <h3>Total Active</h3>
            <div className="summary-value">{summary?.totalActive ?? pagination.total}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">🖥️</div>
          <div className="summary-content">
            <h3>Desktop</h3>
            <div className="summary-value">{summary?.byDeviceType?.desktop ?? 0}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">📱</div>
          <div className="summary-content">
            <h3>Mobile</h3>
            <div className="summary-value">{(summary?.byDeviceType?.mobile ?? 0) + (summary?.byDeviceType?.tablet ?? 0)}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">❓</div>
          <div className="summary-content">
            <h3>Unknown</h3>
            <div className="summary-value">{summary?.byDeviceType?.unknown ?? 0}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <FilterBar filters={filters} onFilterChange={handleFilterChange} onClear={handleClearFilters} />

      {/* Sessions Table */}
      <div className="session-table-container">
        {sessions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3>No Sessions Found</h3>
            <p>
              {Object.keys(filters).length > 0
                ? 'No sessions match your current filters. Try adjusting or clearing filters.'
                : 'There are no active sessions in your organization.'}
            </p>
          </div>
        ) : (
          <>
            <table className="session-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Device</th>
                  <th>IP Address</th>
                  <th>Started</th>
                  <th>Last Active</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr key={session.sessionId}>
                    <td>
                      <div className="user-cell">
                        <strong>{session.userEmail}</strong>
                        <span className="user-role">{session.role}</span>
                      </div>
                    </td>
                    <td>
                      <div className="device-cell">
                        <span className="device-icon">
                          {(DEVICE_TYPE_INFO[session.deviceInfo?.type as DeviceType] || DEVICE_TYPE_INFO.unknown).icon}
                        </span>
                        <span className="device-info">
                          {session.deviceInfo?.browser || 'Unknown'}
                          <span className="device-os">{session.deviceInfo?.os || ''}</span>
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="ip-address">{session.ipAddress}</span>
                    </td>
                    <td>
                      <span className="session-age" title={new Date(session.createdAt).toLocaleString()}>
                        {getSessionAge(session.createdAt)}
                      </span>
                    </td>
                    <td>
                      <span className="last-active" title={new Date(session.lastActivityAt).toLocaleString()}>
                        {formatDate(session.lastActivityAt)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${session.isActive ? 'active' : 'inactive'}`}>
                        {session.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn view" onClick={() => handleViewDetails(session)}>
                          View
                        </button>
                        {session.isActive && (
                          <button
                            className="action-btn terminate"
                            onClick={() => setConfirmTerminate(session)}
                          >
                            Terminate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="pagination">
              <div className="pagination-info">
                Showing {pagination.offset + 1}-{Math.min(pagination.offset + sessions.length, pagination.total)}{' '}
                of {pagination.total} sessions
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  disabled={pagination.offset === 0}
                  onClick={() => setPagination((p) => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))}
                >
                  Previous
                </button>
                <button
                  className="pagination-btn"
                  disabled={!pagination.has_more}
                  onClick={() => setPagination((p) => ({ ...p, offset: p.offset + p.limit }))}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Session Detail Modal */}
      {detailSession && (
        <SessionDetailModal
          session={detailSession.session}
          timeline={detailSession.timeline}
          onClose={() => setDetailSession(null)}
          onTerminate={() => {
            setDetailSession(null);
            setConfirmTerminate(detailSession.session);
          }}
        />
      )}

      {/* Confirm Terminate Session Modal */}
      {confirmTerminate && (
        <ConfirmModal
          title="Terminate Session"
          message={`Are you sure you want to terminate the session for ${confirmTerminate.userEmail}? They will be logged out immediately and need to sign in again.`}
          confirmLabel="Terminate Session"
          onConfirm={handleTerminateSession}
          onCancel={() => setConfirmTerminate(null)}
          isLoading={isProcessing}
        />
      )}

      {/* Confirm Terminate All User Sessions Modal */}
      {confirmTerminateUser && (
        <ConfirmModal
          title="Terminate All User Sessions"
          message={`Are you sure you want to terminate ALL sessions for ${confirmTerminateUser.email}? They will be logged out from all devices immediately.`}
          confirmLabel="Terminate All Sessions"
          onConfirm={handleTerminateUserSessions}
          onCancel={() => setConfirmTerminateUser(null)}
          isLoading={isProcessing}
        />
      )}
    </div>
  );
};

export default SessionMonitor;
